import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Upload, FileSpreadsheet } from "lucide-react";

export const AdminBulkUpload = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [source, setSource] = useState("Bulk Upload");
  const [attribution, setAttribution] = useState("MacroVault");
  const [publishImmediately, setPublishImmediately] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState<any>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setResults(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("Please select a file");
      return;
    }

    setUploading(true);
    setResults(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error("Not authenticated");
        navigate("/auth");
        return;
      }

      const formData = new FormData();
      formData.append("file", file);
      formData.append("source", source);
      formData.append("attribution", attribution);
      formData.append("publishImmediately", publishImmediately.toString());

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/bulk-upload-macros`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
          body: formData,
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Upload failed");
      }

      setResults(result);
      toast.success(`Successfully processed ${result.created} macros`);
      setFile(null);
    } catch (error: any) {
      console.error("Upload error:", error);
      toast.error(error.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Upload className="h-5 w-5" />
          Bulk Upload Macros
        </CardTitle>
        <CardDescription>
          Upload an Excel file (.xlsx) with VBA code. AI will auto-generate names, descriptions, and assign categories.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="file">Excel File (.xlsx)</Label>
          <div className="flex items-center gap-4">
            <Input
              id="file"
              type="file"
              accept=".xlsx"
              onChange={handleFileChange}
              disabled={uploading}
            />
            {file && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <FileSpreadsheet className="h-4 w-4" />
                {file.name}
              </div>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="source">Source</Label>
          <Input
            id="source"
            value={source}
            onChange={(e) => setSource(e.target.value)}
            placeholder="e.g., Excel Forum, User Submission"
            disabled={uploading}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="attribution">Attribution</Label>
          <Input
            id="attribution"
            value={attribution}
            onChange={(e) => setAttribution(e.target.value)}
            placeholder="e.g., MacroVault, John Doe"
            disabled={uploading}
          />
        </div>

        <div className="flex items-center space-x-2">
          <Checkbox
            id="publish"
            checked={publishImmediately}
            onCheckedChange={(checked) => setPublishImmediately(checked as boolean)}
            disabled={uploading}
          />
          <Label htmlFor="publish" className="cursor-pointer">
            Publish immediately (skip pending review)
          </Label>
        </div>

        <Button
          onClick={handleUpload}
          disabled={!file || uploading}
          className="w-full"
        >
          {uploading ? "Uploading..." : "Upload Macros"}
        </Button>

        {results && (
          <Card className="border-primary/20">
            <CardHeader>
              <CardTitle className="text-lg">Upload Results</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-6 text-sm">
                <p>
                  <span className="font-medium">Processed:</span> {results.processed}
                </p>
                <p className="text-green-600">
                  <span className="font-medium">Created:</span> {results.created}
                </p>
                {results.failed > 0 && (
                  <p className="text-destructive">
                    <span className="font-medium">Failed:</span> {results.failed}
                  </p>
                )}
              </div>

              {results.results && results.results.length > 0 && (
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {results.results.map((result: any, idx: number) => (
                    <div
                      key={idx}
                      className={`p-3 rounded-lg border ${
                        result.status === 'created'
                          ? 'bg-green-50 border-green-200 dark:bg-green-950 dark:border-green-800'
                          : 'bg-destructive/10 border-destructive/30'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{result.name}</p>
                          {result.status === 'created' && result.aiGenerated && (
                            <p className="text-xs text-muted-foreground mt-1">
                              AI Generated:{' '}
                              {result.aiGenerated.name && 'Name, '}
                              {result.aiGenerated.shortDesc && 'Description, '}
                              Category: {result.aiGenerated.category}
                            </p>
                          )}
                          {result.status === 'failed' && (
                            <p className="text-xs text-destructive mt-1">{result.error}</p>
                          )}
                        </div>
                        {result.status === 'created' && result.slug && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/m/${result.slug}`)}
                          >
                            View
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Excel Format</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Your Excel file can have these columns (only Code is required):
            </p>
            <ul className="space-y-2 text-sm">
              <li>
                <span className="font-medium text-primary">Code</span> (or "VBA Code") - <span className="font-medium">Required</span> - The VBA code
              </li>
              <li>
                <span className="font-medium">Name</span> (or "Macro Name") - Optional (AI generates if missing)
              </li>
              <li>
                <span className="font-medium">Short Description</span> (or "Description") - Optional (AI generates if missing)
              </li>
              <li>
                <span className="font-medium">Notes</span> - Optional (Extra context for AI to improve generation)
              </li>
            </ul>
            <p className="text-xs text-muted-foreground mt-4">
              💡 The AI will automatically generate names, descriptions, and assign categories based on the VBA code analysis.
            </p>
          </CardContent>
        </Card>
      </CardContent>
    </Card>
  );
};
